/**
 * Console data and configuration board.
 *
 * The recommendation engine reads two things an operator could not see: the
 * district catalog and the reference data stored behind it. A round that failed on
 * coverage therefore said only that some area lacked a place — never whether the
 * catalog is short, whether that activity was ever fetched, or whether the fill
 * failed. This page is that missing view.
 *
 * It is read-only. The district list is collapsed to two lines per region, like
 * every other console list, so paging never means scrolling past the whole catalog.
 */

import { useTranslation } from 'react-i18next'

import type { AdminDatasetStatusDto } from '@shared/types'

import { LoadingBlock, ErrorBlock } from '../../components/layout/states'
import { Badge } from '../../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Stat, StatGrid } from '../../components/ui/primitives'
import { useAdminDataStatus } from '../../hooks/use-admin'
import { activityLabelKey, formatUnixSeconds, joinList } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { AdminPage, CollapsibleRow, Pager, usePageParam } from './shared'

/** Colour for a dataset's refresh result. */
function datasetVariant(dataset: AdminDatasetStatusDto): 'success' | 'warning' | 'destructive' {
  if (dataset.status === 'error') {
    return 'destructive'
  }
  return dataset.stale ? 'warning' : 'success'
}

export function AdminDataStatusPage() {
  const { t } = useTranslation()
  const { page, goToPage } = usePageParam()
  const status = useAdminDataStatus(page)
  const locale = currentLocale()

  const data = status.data
  const districts = data?.districts

  /** A timestamp the console can read, or the explicit "never" marker. */
  const stamp = (seconds: number | null): string =>
    seconds === null ? t('admin.dataStatus.never') : formatUnixSeconds(seconds, locale)

  return (
    <AdminPage
      title={t('admin.dataStatus.title')}
      description={t('admin.dataStatus.description')}
      current={t('admin.nav.dataStatus')}
    >
      {status.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
      {status.isError ? (
        <ErrorBlock
          title={t('admin.common.error')}
          retryLabel={t('admin.common.retry')}
          onRetry={() => void status.refetch()}
        />
      ) : null}

      {data ? (
        <>
          <StatGrid className="grid-cols-2">
            <Stat label={t('admin.dataStatus.regions')} value={data.districts.regions} />
            <Stat label={t('admin.dataStatus.spots')} value={data.districts.spots} />
            <Stat label={t('admin.dataStatus.storedPlaces')} value={data.counts.referencePois} />
            <Stat label={t('admin.dataStatus.anchors')} value={data.counts.anchors} />
          </StatGrid>

          <Card>
            <CardHeader>
              <CardTitle>{t('admin.dataStatus.datasetsTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 text-sm">
              {data.datasets.map((dataset) => (
                <div key={dataset.key} className="flex flex-wrap items-center gap-2">
                  <Badge variant={datasetVariant(dataset)}>{t(`reference.${dataset.key}`)}</Badge>
                  <span className="text-muted-foreground">
                    {t('admin.dataStatus.datasetRecords', { count: dataset.recordCount })}
                    {' · '}
                    {stamp(dataset.lastRefreshedAt)}
                  </span>
                  {dataset.lastError ? (
                    <span className="text-destructive text-xs">{dataset.lastError}</span>
                  ) : null}
                </div>
              ))}
              <p className="text-muted-foreground text-xs">
                {t('admin.dataStatus.fillMarks', { count: data.counts.fillMarks })}
                {' · '}
                {t('admin.dataStatus.lastDepartures', { count: data.counts.lastDepartures })}
                {' · '}
                {t('admin.dataStatus.travelModelParams', { count: data.counts.travelModelParams })}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('admin.dataStatus.coverageTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-col gap-1.5 text-sm">
              {data.coverage.length === 0 ? (
                <p className="text-muted-foreground">{t('admin.dataStatus.coverageEmpty')}</p>
              ) : (
                data.coverage.map((entry) => (
                  <div key={entry.activity} className="flex flex-wrap items-center gap-2">
                    <span>{t(activityLabelKey(entry.activity))}</span>
                    <span className="text-muted-foreground">
                      {t('admin.dataStatus.coverageRow', {
                        places: entry.poiCount,
                        districts: entry.districtCount,
                      })}
                    </span>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>{t('admin.dataStatus.anchorsTitle')}</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3 text-sm">
              {data.anchors.map((entry) => (
                <span key={entry.kind} className="text-muted-foreground">
                  {t(`anchor.${entry.kind}`)}: {entry.count}
                </span>
              ))}
            </CardContent>
          </Card>

          <section className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold">{t('admin.dataStatus.districtsTitle')}</h2>
            <Pager
              page={districts?.page ?? 1}
              pageSize={districts?.pageSize ?? 20}
              total={districts?.regions ?? 0}
              onPage={goToPage}
            />
            {districts?.items.map((district) => (
              <CollapsibleRow
                key={district.id}
                title={<span className="text-sm font-medium">{district.name}</span>}
                meta={
                  <span className="text-muted-foreground truncate text-xs">
                    {t('admin.dataStatus.radius', { meters: district.radiusMeters })}
                    {' · '}
                    {district.coveredActivities.length === 0
                      ? t('admin.dataStatus.noCoverage')
                      : t('admin.dataStatus.covered', {
                          activities: joinList(
                            district.coveredActivities.map((activity) =>
                              t(activityLabelKey(activity)),
                            ),
                            t,
                          ),
                        })}
                  </span>
                }
                badges={
                  <>
                    <Badge variant="outline">
                      {t('admin.dataStatus.places', { count: district.poiCount })}
                    </Badge>
                    <Badge variant="outline">
                      {t('admin.dataStatus.malls', { count: district.spotCount })}
                    </Badge>
                    <Badge variant="outline">
                      {t('admin.dataStatus.districtAnchors', { count: district.anchorCount })}
                    </Badge>
                  </>
                }
              >
                <dl className="text-muted-foreground grid gap-1 text-xs sm:grid-cols-2">
                  <div>
                    <dt className="inline">{t('admin.dataStatus.id')}: </dt>
                    <dd className="inline">{district.id}</dd>
                  </div>
                  <div>
                    <dt className="inline">{t('admin.dataStatus.coordinates')}: </dt>
                    <dd className="inline">
                      {district.lat.toFixed(4)}, {district.lng.toFixed(4)}
                    </dd>
                  </div>
                  <div>
                    <dt className="inline">{t('admin.dataStatus.address')}: </dt>
                    <dd className="inline">{district.address ?? t('admin.common.noData')}</dd>
                  </div>
                  <div>
                    <dt className="inline">{t('admin.dataStatus.tags')}: </dt>
                    <dd className="inline">
                      {district.tags.length > 0
                        ? joinList(district.tags, t)
                        : t('admin.common.noData')}
                    </dd>
                  </div>
                </dl>
              </CollapsibleRow>
            ))}
            <Pager
              page={districts?.page ?? 1}
              pageSize={districts?.pageSize ?? 20}
              total={districts?.regions ?? 0}
              onPage={goToPage}
            />
          </section>
        </>
      ) : null}
    </AdminPage>
  )
}
